/**
 * Syncs Control Center map/ops state from controller → display via kiosk loopback relay.
 * Regular / non-kiosk: no-op.
 */
import { useEffect, useRef, useState, type RefObject } from 'react'
import type { Map as LeafletMap } from 'leaflet'
import { useKioskRole } from '../../hooks/useKioskRole'
import {
  getKioskState,
  patchKioskPayload,
  subscribeKioskState,
  type KioskStateEnvelope,
} from '../../utils/kioskDisplaySync'
import type { OperationMapPoint, OperationStep } from './operations'
import type {
  BoundaryOverlayToggles,
  MapMarkerVisibility,
  MapStyleId,
  OmniAssetFilters,
  OmniJobFilters,
  OmniMunicipalityFilters,
  OmniPersonFilters,
  OmniSelection,
} from './map/controlCenterMapTypes'
import type { ControlCenterMunicipality } from '../../types/controlCenter'

export type ControlCenterKioskSnapshot = {
  mapCenter: { lat: number; lng: number } | null
  mapZoom: number | null
  mapStyleId: MapStyleId
  weatherEnabled: boolean
  omniSelections: OmniSelection[]
  focusId: string | null
  focusedResultPointId: string | null
  entityFocusToken: number
  resultFocusToken: number
  userLocationPoint: OperationMapPoint | null
  operationSteps: OperationStep[]
  activeStepId: string
  outputOpen: boolean
  externalMunicipality: ControlCenterMunicipality | null
  externalMunicipalitiesById: Record<string, ControlCenterMunicipality>
  /** Toolbar filters: show-all layer toggles + boundary overlays. */
  mapMarkerVisibility: MapMarkerVisibility
  boundaryOverlays: BoundaryOverlayToggles
  /** Filters applied to show-all layers (and omni lists). */
  jobFilters: OmniJobFilters
  personFilters: OmniPersonFilters
  assetFilters: OmniAssetFilters
  municipalityFilters: OmniMunicipalityFilters
}

type ApplyHandlers = {
  setMapStyleId: (v: MapStyleId) => void
  setWeatherEnabled: (v: boolean) => void
  setOmniSelections: (v: OmniSelection[]) => void
  setFocusId: (v: string | null) => void
  setFocusedResultPointId: (v: string | null) => void
  setEntityFocusToken: (v: number | ((n: number) => number)) => void
  setResultFocusToken: (v: number | ((n: number) => number)) => void
  setUserLocationPoint: (v: OperationMapPoint | null) => void
  setOperationSteps: (v: OperationStep[]) => void
  setActiveStepId: (v: string) => void
  setOutputOpen: (v: boolean) => void
  setExternalMunicipality: (v: ControlCenterMunicipality | null) => void
  setExternalMunicipalitiesById: (v: Record<string, ControlCenterMunicipality>) => void
  setMapMarkerVisibility: (v: MapMarkerVisibility) => void
  setBoundaryOverlays: (v: BoundaryOverlayToggles) => void
  setJobFilters: (v: OmniJobFilters) => void
  setPersonFilters: (v: OmniPersonFilters) => void
  setAssetFilters: (v: OmniAssetFilters) => void
  setMunicipalityFilters: (v: OmniMunicipalityFilters) => void
}

function readControlCenter(envelope: KioskStateEnvelope | null): ControlCenterKioskSnapshot | null {
  const raw = envelope?.state?.payload?.controlCenter
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  return raw as ControlCenterKioskSnapshot
}

export function useControlCenterKioskSync(args: {
  active: boolean
  mapRef: RefObject<LeafletMap | null>
  /** Serialized snapshot JSON — memoize upstream to avoid spam. */
  snapshotJson: string
  apply: ApplyHandlers
}): { isDisplayKiosk: boolean; isControllerKiosk: boolean } {
  const role = useKioskRole()
  const isDisplayKiosk = role === 'display'
  const isControllerKiosk = role === 'controller'
  const applyingRemoteRef = useRef(false)
  const lastPublishedJson = useRef('')
  const [cameraEpoch, setCameraEpoch] = useState(0)
  const applyRef = useRef(args.apply)
  applyRef.current = args.apply

  // Controller: bump publish when the map camera moves
  useEffect(() => {
    if (!isControllerKiosk || !args.active) return
    let attached: LeafletMap | null = null

    const onCamera = () => {
      setCameraEpoch((n) => n + 1)
    }

    const tryAttach = () => {
      const map = args.mapRef.current
      if (!map || map === attached) return
      if (attached) {
        attached.off('moveend', onCamera)
        attached.off('zoomend', onCamera)
      }
      attached = map
      map.on('moveend', onCamera)
      map.on('zoomend', onCamera)
    }

    tryAttach()
    const id = window.setInterval(tryAttach, 500)
    return () => {
      window.clearInterval(id)
      if (attached) {
        attached.off('moveend', onCamera)
        attached.off('zoomend', onCamera)
      }
    }
  }, [isControllerKiosk, args.active, args.mapRef])

  // Controller: publish snapshot (debounced)
  useEffect(() => {
    if (!isControllerKiosk || !args.active) return
    if (applyingRemoteRef.current) return

    let parsed: ControlCenterKioskSnapshot
    try {
      parsed = JSON.parse(args.snapshotJson) as ControlCenterKioskSnapshot
    } catch {
      return
    }

    const map = args.mapRef.current
    const center = map?.getCenter()
    const next: ControlCenterKioskSnapshot = {
      ...parsed,
      mapCenter: center ? { lat: center.lat, lng: center.lng } : parsed.mapCenter,
      mapZoom: map != null ? map.getZoom() : parsed.mapZoom,
    }

    const json = JSON.stringify(next)
    if (json === lastPublishedJson.current) return

    const timer = window.setTimeout(() => {
      lastPublishedJson.current = json
      void patchKioskPayload({ controlCenter: next }, 'controller')
    }, 180)

    return () => window.clearTimeout(timer)
  }, [isControllerKiosk, args.active, args.mapRef, args.snapshotJson, cameraEpoch])

  // Display: apply remote snapshot — camera last, after React/Leaflet settle
  useEffect(() => {
    if (!isDisplayKiosk) return

    const lastCameraRef: {
      current: { lat: number; lng: number; zoom: number } | null
    } = { current: null }

    const applyCameraCoords = (lat: number, lng: number, zoom: number) => {
      const map = args.mapRef.current
      if (!map) return
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(zoom)) return
      lastCameraRef.current = { lat, lng, zoom }
      // Match controller geographic center exactly (no fitBounds / focus-anchor).
      map.invalidateSize({ animate: false })
      map.setView([lat, lng], zoom, { animate: false })
    }

    const applyCamera = (cc: ControlCenterKioskSnapshot) => {
      if (!cc.mapCenter || typeof cc.mapZoom !== 'number') return
      applyCameraCoords(cc.mapCenter.lat, cc.mapCenter.lng, cc.mapZoom)
    }

    const reapplyLastCamera = () => {
      const cam = lastCameraRef.current
      if (!cam) return
      applyCameraCoords(cam.lat, cam.lng, cam.zoom)
    }

    const onResize = () => {
      // UI scale applyZoom dispatches resize after layout compensation.
      window.requestAnimationFrame(() => {
        reapplyLastCamera()
        window.setTimeout(reapplyLastCamera, 120)
      })
    }
    window.addEventListener('resize', onResize)

    const applySnapshot = (cc: ControlCenterKioskSnapshot) => {
      const apply = applyRef.current
      applyingRemoteRef.current = true
      apply.setMapStyleId(cc.mapStyleId)
      apply.setWeatherEnabled(Boolean(cc.weatherEnabled))
      apply.setOmniSelections(Array.isArray(cc.omniSelections) ? cc.omniSelections : [])
      apply.setFocusId(cc.focusId ?? null)
      apply.setFocusedResultPointId(cc.focusedResultPointId ?? null)
      if (typeof cc.entityFocusToken === 'number') apply.setEntityFocusToken(cc.entityFocusToken)
      if (typeof cc.resultFocusToken === 'number') apply.setResultFocusToken(cc.resultFocusToken)
      apply.setUserLocationPoint(cc.userLocationPoint ?? null)
      if (Array.isArray(cc.operationSteps) && cc.operationSteps.length > 0) {
        apply.setOperationSteps(cc.operationSteps)
      }
      if (typeof cc.activeStepId === 'string' && cc.activeStepId) {
        apply.setActiveStepId(cc.activeStepId)
      }
      apply.setOutputOpen(cc.outputOpen !== false)
      apply.setExternalMunicipality(cc.externalMunicipality ?? null)
      if (cc.externalMunicipalitiesById && typeof cc.externalMunicipalitiesById === 'object') {
        apply.setExternalMunicipalitiesById(cc.externalMunicipalitiesById)
      }
      if (cc.mapMarkerVisibility && typeof cc.mapMarkerVisibility === 'object') {
        apply.setMapMarkerVisibility(cc.mapMarkerVisibility)
      }
      if (cc.boundaryOverlays && typeof cc.boundaryOverlays === 'object') {
        apply.setBoundaryOverlays(cc.boundaryOverlays)
      }
      if (cc.jobFilters && typeof cc.jobFilters === 'object') {
        apply.setJobFilters(cc.jobFilters)
      }
      if (cc.personFilters && typeof cc.personFilters === 'object') {
        apply.setPersonFilters(cc.personFilters)
      }
      if (cc.assetFilters && typeof cc.assetFilters === 'object') {
        apply.setAssetFilters(cc.assetFilters)
      }
      if (cc.municipalityFilters && typeof cc.municipalityFilters === 'object') {
        apply.setMunicipalityFilters(cc.municipalityFilters)
      }

      // State updates can mount boundary layers; apply camera after paint so
      // nothing else owns the view, then once more after layout/scale settle.
      window.requestAnimationFrame(() => {
        applyCamera(cc)
        window.setTimeout(() => {
          applyCamera(cc)
          applyingRemoteRef.current = false
        }, 120)
      })
    }

    const onEnvelope = (envelope: KioskStateEnvelope | null) => {
      const cc = readControlCenter(envelope)
      if (!cc) return
      applySnapshot(cc)
    }

    void getKioskState().then(onEnvelope)
    const unsubscribe = subscribeKioskState(onEnvelope)
    return () => {
      window.removeEventListener('resize', onResize)
      unsubscribe()
    }
  }, [isDisplayKiosk, args.mapRef])

  return { isDisplayKiosk, isControllerKiosk }
}
